import { Button, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ObjectSchema } from 'yup';
import { formAutocomplete } from '../../../config';
import { uiStateSelectors } from '../../../services/redux/features/uiState.slice';
import { MixedComponentsProps } from '../../../types/mixedComponentProps';
import { FormSectionEditButton } from '../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../FormSectionSummaryTitle';
import { createFormInputs } from './formInputs';
import { TradingDetailsServicePlanFormModelNew } from './formModel';
import { tradingDetailsInitialValues } from './initialValues';
import { createTradingDetailsServicePlanFormValidator } from './validationSchema';

export const TradingDetailsServicePlanFormNew_MX: React.FC<MixedComponentsProps> = ({
  formHelpers,
  titleTranslationKey,
}) => {
  const [initialValues] = useState(tradingDetailsInitialValues);
  const [validationSchema] = useState<ObjectSchema<Partial<TradingDetailsServicePlanFormModelNew>>>(
    createTradingDetailsServicePlanFormValidator
  );

  const { formSectionProps, onFormSubmitSuccess, onStartEditInSummaryMode, isDisabledFormSummary } = formHelpers;

  const onSubmit = (values: TradingDetailsServicePlanFormModelNew) => {
    /* start loader */
    Promise.resolve(/* submit */)
      .then(() => onFormSubmitSuccess(values))
      .catch(/* on error */)
      .finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(
        t(titleTranslationKey ?? `tradingDetailsServicePlanForm:title:soleTrader`),
        isDisabledFormSummary
      )}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent disabled={isDisabledFormSummary} formHelpers={formHelpers} />
      </Formik>
    </FormSection>
  );
};

export const FormContent = (props) => {
  const { submitForm, values } = useFormikContext<TradingDetailsServicePlanFormModelNew>();
  const { disabled, formHelpers } = props;
  const { onFormValuesChange } = formHelpers;

  const snCSubmitted = !useSelector(uiStateSelectors.snCSubmittedMock);
  const disableAllFormControls = disabled || snCSubmitted;

  const [inputs, updateInputs] = useState<ReturnType<typeof createFormInputs>>(() =>
    createFormInputs(disableAllFormControls)
  );

  useEffect(() => {
    updateInputs(createFormInputs(disableAllFormControls));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableAllFormControls]);

  useEffect(() => {
    onFormValuesChange(values);
  }, [values, onFormValuesChange]);

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/3">
          <Button disabled={disableAllFormControls} type="button" secondary icon="address-book" ariaLabel="Search existing customer" onClick={() => ''}>
            {t('tradingDetailsServicePlanForm:searchExistingCustomer')}
          </Button>
          </Layout.Item>
        </Layout>
      </Fieldset.Row>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.tradingAs()}</Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.legalName()}</Layout.Item>
        </Layout>
      </Fieldset.Row>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.vatLiable()}</Layout.Item>
          <Layout.Item default="1/2" s="1/1">{inputs.vatNumber()}</Layout.Item>
        </Layout>
      </Fieldset.Row>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.email()}</Layout.Item>
          <Layout.Item default="1/4" s="1/1">{inputs.countryCode()}</Layout.Item>
          <Layout.Item default="1/4" s="1/1">{inputs.mobileNumber()}</Layout.Item>
        </Layout>
      </Fieldset.Row>
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">{inputs.correspondenceMethod()}</Layout.Item>
        </Layout>
      </Fieldset.Row>
     <FormSectionSubmitButton  onClick={submitForm} formHelpers={formHelpers} />
    </Form>
  );
};
